import FreePlanUsage from "../components/FreePlanUsage";
import { workspaceFeatures } from "../lib/workspaceCapabilities";
import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, useAuth } from "../App";
import { toast } from "@/lib/toast";
import { Loader2, ArrowRight, X } from "lucide-react";
import { jsPDF } from "jspdf";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { formatPlanAmount, formatPlanPrice, normalizePlanCurrency, planPeriodLabel } from "../lib/planPricing";
import PaymentHistory, { paymentAmount, paymentDate } from "../components/PaymentHistory";
import { pendingPromotion, savedPromotion, rememberClaimedPromotion, forgetPromotion, PROMOTION_AUTOFILL_MS } from "../lib/referrals";
import { metaCheckoutContext, trackInitiateCheckout, verifyAndTrackPurchase } from "../lib/metaPixel";
import "./PlansPaymentDialogs.css";

const recurringInterval = (plan) => plan?.billing_period === "yearly" ? "12 meses" : plan?.billing_period === "quarterly" ? "3 meses" : "mes";
const PlansPage = () => {
  const { user, appSettings, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyError, setHistoryError] = useState("");
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [expandedBenefits, setExpandedBenefits] = useState({});
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [cancellationOpen, setCancellationOpen] = useState(false);
  const [cancellationChallenge, setCancellationChallenge] = useState(null);
  const [cancellationOtp, setCancellationOtp] = useState("");
  const [cancellationError, setCancellationError] = useState("");
  const [sendingCancellationOtp, setSendingCancellationOtp] = useState(false);

  const openCancellation = () => {
    setCancellationChallenge(null); setCancellationOtp(""); setCancellationError(""); setCancellationOpen(true);
  };
  const requestCancellationOtp = async () => {
    setSendingCancellationOtp(true); setCancellationError("");
    try {
      const { data } = await api.post("/billing/request-cancellation-otp");
      if (!data.whatsapp_sent || !data.subscription_id) throw new Error("No pudimos enviar el código a tu WhatsApp.");
      setCancellationChallenge(data); setCancellationOtp("");
    } catch (error) { setCancellationError(error.response?.data?.detail || error.message || "No pudimos enviar el código. Intenta de nuevo."); }
    finally { setSendingCancellationOtp(false); }
  };
  const [downloadingReceipt, setDownloadingReceipt] = useState(null);
  const [promotionCode, setPromotionCode] = useState(() => savedPromotion(user?.id)?.code || pendingPromotion() || "");
  const [promotionExpiry, setPromotionExpiry] = useState(() => savedPromotion(user?.id)?.expiresAt || Date.now() + PROMOTION_AUTOFILL_MS);
  const promotionEdited = useRef(false);
  const [promotionQuotes, setPromotionQuotes] = useState({});
  const [appliedCode, setAppliedCode] = useState("");
  const [validatingCode, setValidatingCode] = useState(false);
  const [promotionError, setPromotionError] = useState("");
  const [pendingCheckout, setPendingCheckout] = useState(null);
  const [checkoutBlocked, setCheckoutBlocked] = useState(false);
  const [checkoutBlockMessage, setCheckoutBlockMessage] = useState("");
  const checkoutBusy = useRef(false);
  const checkoutBlockedRef = useRef(false);
  const applyCheckoutState = useCallback(data => {
    checkoutBlockedRef.current = Boolean(data.blocked || data.checkout);
    setPendingCheckout(data.checkout || null);
    setCheckoutBlocked(Boolean(data.blocked || data.checkout));
    setCheckoutBlockMessage(data.message || "");
  }, []);
  useEffect(() => {
    if (!pendingCheckout) return;
    const timer = setInterval(() => {
      void api.get('/billing/pending-checkout').then(({ data }) => applyCheckoutState(data)).catch(() => {});
    }, 30000);
    return () => clearInterval(timer);
  }, [pendingCheckout, applyCheckoutState]);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [paymentMessage, setPaymentMessage] = useState("");
  const returnedReference = searchParams.get("ref_payco") || searchParams.get("x_ref_payco") || "";
  const referenceStorageKey = `aorra:epayco-reference:${user?.id}`;
  const [epaycoReference, setEpaycoReference] = useState(() => returnedReference || sessionStorage.getItem(referenceStorageKey) || "");
  const checkedReturn = useRef(null);
  const paymentCheckBusy = useRef(false);
  const planRequest = useRef(0);

  const clearPromotion = () => {
    promotionEdited.current = true;
    forgetPromotion(user?.id);
    setPromotionCode(""); setAppliedCode(""); setPromotionQuotes({}); setPromotionError("");
    void api.delete("/promotions/pending-code").catch(() => {});
  };

  useEffect(() => {
    let live = true;
    api.get("/promotions/pending-code").then(({ data }) => {
      if (!live || promotionEdited.current || !data.code || !data.expires_at) return;
      const expiry = new Date(data.expires_at).getTime();
      if (expiry <= Date.now()) return;
      setPromotionCode(data.code); setPromotionExpiry(rememberClaimedPromotion(user?.id, data.code, data.expires_at));
    }).catch(() => {});
    return () => { live = false; };
  }, [user?.id]);

  useEffect(() => {
    if (!promotionCode) return;
    const timer = setTimeout(() => {
      forgetPromotion(user?.id);
      setPromotionCode(""); setAppliedCode(""); setPromotionQuotes({}); setPromotionError("");
    }, Math.max(0, promotionExpiry - Date.now()));
    return () => clearTimeout(timer);
  }, [promotionCode, promotionExpiry, user?.id]);

  const applyPromotion = async () => {
    const code = promotionCode.trim().toUpperCase();
    if (!code) return;
    setValidatingCode(true);
    setPromotionError("");
    try {
      const responses = await Promise.all(plans.map(async plan => {
        try { return [plan.id, (await api.post("/promotions/quote", { code, plan_id: plan.id, recurring: paymentGateways.mp_active && !paymentGateways.epayco_active })).data]; }
        catch (error) { return [plan.id, { error: error.response?.data?.detail || "No se pudo validar este plan." }]; }
      }));
      if (!responses.some(([, value]) => !value.error)) throw new Error(responses[0]?.[1]?.error || "No hay planes elegibles.");
      const claim = await api.post("/promotions/claim", { code });
      setPromotionExpiry(rememberClaimedPromotion(user.id, code, claim.data.expires_at));
      setPromotionQuotes(Object.fromEntries(responses));
      setAppliedCode(code);
    } catch (error) {
      setPromotionQuotes({}); setAppliedCode("");
      setPromotionError(error.response?.data?.detail || error.message);
    } finally { setValidatingCode(false); }
  };

  const handleDownloadReceipt = (idx) => {
    if (!history[idx]?.can_download) return;
    setDownloadingReceipt(idx);
    toast.info("Generando recibo...", { id: "receipt" });
    
    setTimeout(() => {
      try {
        const item = history[idx];
        const doc = new jsPDF();
        
        // Setup colors and fonts
        doc.setFillColor(245, 246, 250);
        doc.rect(0, 0, 210, 40, 'F');
        
        doc.setTextColor(27, 31, 58);
        doc.setFontSize(22);
        doc.setFont("helvetica", "bold");
        doc.text("Aorra - Recibo de Pago", 20, 25);
        
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(107, 114, 128);
        
        const dateStr = paymentDate(item);
        
        doc.text(`Fecha: ${dateStr}`, 140, 25);
        
        // Body
        doc.setTextColor(27, 31, 58);
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("Detalles de la transacción:", 20, 60);
        
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        
        doc.text(`Descripción:`, 20, 80);
        doc.text(doc.splitTextToSize(item.title + " - " + item.desc, 130), 60, 80);
        
        doc.text(`Monto:`, 20, 95);
        const amountStr = paymentAmount(item);
        doc.text(amountStr, 60, 95);
        
        doc.text(`Estado:`, 20, 110);
        doc.text(item.status_label, 60, 110);
        
        doc.text(`Usuario:`, 20, 125);
        doc.text(user?.email || user?.phone || "N/A", 60, 125);
        
        doc.text(`Referencia: ${item.reference}`, 20, 140);

        // Footer
        doc.setDrawColor(229, 231, 235);
        doc.line(20, 150, 190, 150);
        
        doc.setFontSize(10);
        doc.setTextColor(156, 163, 175);
        doc.text("Este recibo es un comprobante de pago generado electrónicamente.", 105, 160, { align: "center" });
        
        doc.save(`Recibo_Aorra_${dateStr.replace(/\//g, '-')}.pdf`);
        
        toast.success("Recibo descargado correctamente", { id: "receipt" });
      } catch (error) {
        console.error("Error generating PDF:", error);
        toast.error("Error al generar el recibo", { id: "receipt" });
      } finally {
        setDownloadingReceipt(null);
      }
    }, 1000);
  };
  
  const [paymentGateways, setPaymentGateways] = useState({ mp_active: false, epayco_active: false });
  const [gatewayModalOpen, setGatewayModalOpen] = useState(false);
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState(null);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const [payerEmail, setPayerEmail] = useState(user?.email || "");
  const [subscriptionError, setSubscriptionError] = useState("");

  const fetchData = useCallback(async () => {
    const requestId = ++planRequest.current;
    setLoadError("");
    try {
      const [plansRes, subRes, histRes, gatewaysRes] = await Promise.all([
        api.get("/billing/plans", { timeout: 12000 }),
        api.get("/billing/my-subscription", { timeout: 12000 }),
        api.get("/billing/history", { timeout: 12000 }).catch(() => ({ data: null })),
        api.get("/public/payment-gateways", { timeout: 12000 }).catch(() => ({ data: { mp_active: false, epayco_active: true } }))
      ]);
      if (requestId !== planRequest.current) return;
      setPlans(plansRes.data.plans || []);
      setSubscription(subRes.data);
      setPaymentGateways(gatewaysRes.data);
      api.get("/billing/pending-checkout").then(response => {
        if (requestId === planRequest.current) applyCheckoutState(response.data);
      }).catch(() => {});
      
      // Load ePayco script if active
      if (gatewaysRes.data.epayco_active && !document.getElementById('epayco-script')) {
        const script = document.createElement('script');
        script.id = 'epayco-script';
        script.src = 'https://checkout.epayco.co/checkout.js';
        script.async = true;
        document.body.appendChild(script);
      }
      
      const entries = histRes.data?.entries;
      setHistory(Array.isArray(entries) ? entries : []);
      setHistoryError(Array.isArray(entries) ? "" : "No pudimos cargar tu historial de pagos. Intenta nuevamente.");
      
    } catch (error) {
      console.error("Error fetching plans:", error);
      if (requestId === planRequest.current) setLoadError("No pudimos cargar tu plan. Puedes intentarlo de nuevo sin repetir el pago.");
    } finally {
      if (requestId === planRequest.current) setLoading(false);
    }
  }, [applyCheckoutState]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const status = searchParams.get("status");
    if (status === "success" || status === "epayco_response") {
      toast.info("Estamos verificando tu pago");
    } else if (status === "failure") {
      toast.error("El pago no pudo ser procesado");
    } else if (status === "pending") {
      toast.info("Pago pendiente");
    } else if (status === "subscription_created") {
      toast.info("Estamos verificando tu suscripción");
    }
  }, [searchParams]);

  const handleSubscribe = async (plan) => {
    if (checkoutBusy.current || paymentCheckBusy.current) return;
    if (checkoutBlockedRef.current && !await checkPendingPayment("")) return;
    if (promotionCode.trim() && appliedCode !== promotionCode.trim().toUpperCase()) {
      setPromotionError("Aplica el código o quítalo antes de continuar al pago.");
      return;
    }
    if (appliedCode && promotionQuotes[plan.id]?.error) {
      setPromotionError(promotionQuotes[plan.id].error);
      return;
    }
    // If both active, show modal
    if (paymentGateways.mp_active && paymentGateways.epayco_active) {
      setSelectedPlanForPayment(plan);
      setGatewayModalOpen(true);
    } else if (paymentGateways.epayco_active) {
      processEpayco(plan);
    } else if (paymentGateways.mp_active) {
      processMercadoPago(plan);
    } else {
      toast.error("No hay pasarelas de pago configuradas");
    }
  };

  const checkPendingPayment = useCallback(async (returnedRef) => {
    if (paymentCheckBusy.current) return;
    paymentCheckBusy.current = true;
    const reference = (typeof returnedRef === "string" ? returnedRef : epaycoReference).trim();
    if (reference) sessionStorage.setItem(referenceStorageKey, reference);
    setCheckingPayment(true);
    setPaymentMessage("Consultando el estado del pago…");
    try {
      const { data } = await api.post("/billing/reconcile-checkout", reference ? { ref_payco: reference } : {}, { timeout: 20000 });
      if (data.status === "approved") {
        if (reference) void verifyAndTrackPurchase(api, 'epayco', reference);
        sessionStorage.removeItem(referenceStorageKey);
        setPaymentMessage("Pago confirmado. Tu plan está activo.");
        toast.success("Pago confirmado. Tu plan está activo.");
        if (refreshUser) {
          try { await refreshUser(); }
          catch (_) { toast.info("Pago confirmado. Recarga el portal para actualizar tu acceso."); }
        }
      } else if (data.needs_epayco_reference) {
        setPaymentMessage("Aún esperamos la confirmación de ePayco. Si pagaste, vuelve desde el enlace de tu comprobante para verificarlo.");
      } else {
        setPaymentMessage(data.message || "Estamos esperando la confirmación del cobro. Puedes revisar su estado nuevamente sin repetir la suscripción.");
      }
      await fetchData();
      const state = await api.get("/billing/pending-checkout", { timeout: 12000 });
      applyCheckoutState(state.data);
      const canContinue = data.status !== "approved" && !state.data.blocked && !state.data.checkout;
      if (canContinue) setPaymentMessage("El intento anterior quedó cerrado. Puedes continuar con tu pago.");
      return canContinue;
    } catch (error) {
      const message = error.response?.data?.detail || "No pudimos consultar el pago. Intenta revisar su estado de nuevo sin repetir la compra.";
      setPaymentMessage(message);
      toast.error(message);
      return false;
    } finally { setCheckingPayment(false); paymentCheckBusy.current = false; }
  }, [epaycoReference, referenceStorageKey, refreshUser, fetchData, applyCheckoutState]);

  useEffect(() => {
    if (searchParams.get("status") === "subscription_created" && checkedReturn.current !== "mercado-subscription") {
      checkedReturn.current = "mercado-subscription";
      checkPendingPayment("");
    }
  }, [searchParams, checkPendingPayment]);

  useEffect(() => {
    if (returnedReference && checkedReturn.current !== returnedReference) {
      checkedReturn.current = returnedReference;
      setEpaycoReference(returnedReference);
      checkPendingPayment(returnedReference);
    }
  }, [returnedReference, checkPendingPayment]);

  const mercadoPaymentId = searchParams.get('payment_id') || searchParams.get('collection_id') || '';
  useEffect(() => {
    if (!mercadoPaymentId) return;
    let cancelled = false;
    let timer;
    let attempts = 0;
    const verify = async () => {
      const tracked = await verifyAndTrackPurchase(api, 'mercadopago', mercadoPaymentId);
      // The approved provider redirect can precede the payment webhook.
      if (!cancelled && !tracked && ++attempts < 5) timer = setTimeout(verify, 5000);
    };
    void verify();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [mercadoPaymentId, searchParams]);

  const processMercadoPago = (plan) => {
    setSubscriptionPlan(plan);
    setSubscriptionError("");
    setSubscriptionModalOpen(true);
  };

  const createMercadoSubscription = async (event) => {
    event.preventDefault();
    const plan = subscriptionPlan;
    if (!plan || checkoutBusy.current || checkoutBlockedRef.current) return;
    checkoutBusy.current = true;
    setProcessingPlan(plan.id);
    setSubscriptionError("");
    try {
      // A one-time promotion must never become a permanent automatic discount.
      if (appliedCode) await api.post("/promotions/quote", { code: appliedCode, plan_id: plan.id, recurring: true });
      const res = await api.post("/billing/create-subscription", { plan_id: plan.id, payer_email: payerEmail.trim(), meta_context: metaCheckoutContext(), ...(appliedCode ? { promotion_code: appliedCode } : {}) });
      if (res.data.init_point) {
        trackInitiateCheckout(res.data, plan.id, `checkout:mercadopago:${res.data.preapproval_id}`);
        window.location.href = res.data.init_point;
      } else {
        setSubscriptionError("No recibimos el enlace de autorización. Revisa el estado de tu suscripción antes de intentar de nuevo.");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "No pudimos confirmar la creación. Revisa el estado antes de volver a suscribirte.";
      setSubscriptionError(message);
      toast.error(message);
    } finally {
      checkoutBusy.current = false;
      setProcessingPlan(null);
      void fetchData();
    }
  };
  
  const processEpayco = async (plan) => {
    if (!plan || checkoutBusy.current || checkoutBlockedRef.current) return;
    checkoutBusy.current = true;
    setProcessingPlan(plan.id);
    try {
      const res = await api.post("/billing/create-epayco-checkout", { plan_id: plan.id, meta_context: metaCheckoutContext(), ...(appliedCode ? { promotion_code: appliedCode } : {}) });
      
      if (window.ePayco) {
        var handler = window.ePayco.checkout.configure({
          key: res.data.key,
          test: res.data.test === "true"
        });
        
        handler.open(res.data);
        if (res.data.test !== 'true') trackInitiateCheckout(res.data, plan.id, `checkout:epayco:${res.data.invoice}`);
      } else {
        toast.error("El checkout de ePayco no está cargado");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al procesar con ePayco");
    } finally {
      checkoutBusy.current = false;
      setProcessingPlan(null);
      void fetchData();
    }
  };

  const handleCancelSubscription = async (event) => {
    event.preventDefault();
    if (!cancellationChallenge || !/^\d{6}$/.test(cancellationOtp)) return;
    setCancellingSubscription(true); setCancellationError("");
    try {
      const res = await api.post("/billing/cancel-subscription", { subscription_id: cancellationChallenge.subscription_id, otp: cancellationOtp });
      toast.success(res.data.message || "Cobros automáticos cancelados");
      setCancellationOpen(false); setCancellationChallenge(null); setCancellationOtp("");
      setPaymentMessage(res.data.message || "Cobros automáticos cancelados. Conservas tu período pagado.");
      await fetchData();
    } catch (error) {
      setCancellationError((error.response?.data?.detail || "No pudimos confirmar la cancelación.") + " Solicita un nuevo código para reintentar.");
      setCancellationChallenge(null); setCancellationOtp("");
    } finally {
      setCancellingSubscription(false);
    }
  };

  if (loading) {
    return (
      <div role="status" className="flex items-center justify-center gap-3 min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        Cargando tu plan…
      </div>
    );
  }

  if (loadError) {
    return <div role="alert" className="mx-auto max-w-xl rounded-2xl bg-white p-6">
      <p>{loadError}</p>
      <button type="button" onClick={fetchData} className="mt-4 font-bold text-blue-600 underline">Reintentar</button>
    </div>;
  }

  const isSubscribed = subscription?.is_subscribed;
  const currentPlanName = subscription?.subscription?.plan_name || (isSubscribed ? "Suscripción activa" : "Plan Gratuito");
  const paidEnd = subscription?.access_policy?.paid_ends_at || subscription?.subscription?.current_period_end;
  const currentPlanEnd = paidEnd
    ? new Date(paidEnd)
    : null;
  const currentPlanEndStr = currentPlanEnd ? currentPlanEnd.toLocaleDateString('es-CO') : "-";
  const recurringSubscription = subscription?.recurring_subscription;
  
  let daysRemaining = 0;
  let progressPercent = 0;
  
  if (isSubscribed && currentPlanEnd) {
    const end = currentPlanEnd;
    const now = new Date();
    daysRemaining = Math.max(0, Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    progressPercent = Math.min(100, Math.max(0, (daysRemaining / 30) * 100));
  } else if (subscription?.is_trial) {
    daysRemaining = subscription.trial_days_remaining || 0;
    progressPercent = Math.min(100, Math.max(0, (daysRemaining / 14) * 100));
  }

  const activePaidPlans = plans.filter(p => p.is_active && p.price > 0);
  const displayPlans = activePaidPlans.length > 0 ? activePaidPlans : [{
    id: "premium",
    name: "Plan Premium",
    price: 20000,
    currency: "COP",
    billing_period: "monthly",
    description: "Escala al siguiente nivel con todas las herramientas.",
    features: ["Cuentas ilimitadas", "Reportes IA", "Prioridad SOPORTE", "Recordatorios de pago"]
  }];

  return (
    <div className="max-w-[1400px] mx-auto pb-12 overflow-x-hidden pt-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
      
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "30px" }}>
        <h1 style={{
          margin: 0,
          fontFamily: "'Manrope', sans-serif",
          fontWeight: 700,
          fontSize: "24px",
          lineHeight: "33px",
          color: "#255E99"
        }}>Mi plan</h1>
      </div>

      <div className="aorra-plans-layout">
        <div className="aorra-promotion-panel">
          <form onSubmit={event => { event.preventDefault(); applyPromotion(); }} className="w-full rounded-2xl border border-blue-100 bg-white p-4">
            <label htmlFor="promotion-code" className="text-sm font-bold text-[#1B2559]">Código de descuento o referido</label>
            <div className="mt-2 flex gap-2"><input id="promotion-code" value={promotionCode} maxLength={32} autoCapitalize="characters" autoComplete="off" placeholder="Ej. REF-ABCDEFGH o LAURA20" onChange={event => { promotionEdited.current = true; setPromotionCode(event.target.value.toUpperCase()); setPromotionExpiry(Date.now() + PROMOTION_AUTOFILL_MS); setAppliedCode(""); setPromotionQuotes({}); setPromotionError(""); }} className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800" /><button disabled={validatingCode || !promotionCode.trim()} type="submit" className="rounded-xl bg-[#396BE9] px-4 py-2 text-sm font-bold text-white disabled:opacity-50">{validatingCode ? "Validando…" : "Aplicar"}</button></div>
            {promotionCode && <button type="button" onClick={clearPromotion} className="mt-2 text-xs text-slate-500 underline">Quitar código</button>}
            {appliedCode && <p role="status" className="mt-2 text-xs text-emerald-700">Código {appliedCode} aplicado. Revisa el precio final de tu plan.</p>}
            {promotionError && <p role="alert" className="mt-2 text-xs text-red-600">{promotionError}</p>}
          </form>
          {paymentMessage && <div className="w-full text-sm text-slate-700"><p role="status">{paymentMessage}</p>
            {epaycoReference && !paymentMessage.startsWith("Pago confirmado") && <button type="button" disabled={checkingPayment} onClick={() => checkPendingPayment(epaycoReference)} className="mt-2 font-bold text-blue-600 underline">{checkingPayment ? "Consultando…" : "Revisar estado del pago"}</button>}
          </div>}
          {(recurringSubscription || searchParams.get("status") === "subscription_created") && <div className="w-full rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-slate-700">
            <p className="font-bold">{recurringSubscription?.last_payment_id ? "Renovación automática con Mercado Pago" : recurringSubscription?.mp_status === 'authorized' ? 'Cobros automáticos autorizados con Mercado Pago' : "Suscripción pendiente de confirmar el primer cobro"}</p>
            <p className="mt-1">Para cambiar de plan o de medio de pago, cancela primero los próximos cobros automáticos.</p>
            {recurringSubscription?.next_payment_date && recurringSubscription?.mp_status === "authorized" && <p className="mt-1">Próximo cobro: {new Date(recurringSubscription.next_payment_date).toLocaleDateString("es-CO")}.</p>}
            <button type="button" disabled={checkingPayment} onClick={() => checkPendingPayment("")} className="mt-2 font-bold text-blue-600 underline">{checkingPayment ? "Consultando…" : "Revisar estado de la suscripción"}</button>
            {subscription?.can_cancel && (!subscription?.is_subscribed || recurringSubscription?.id !== subscription?.subscription?.id) && <button type="button" disabled={cancellingSubscription} onClick={openCancellation} className="ml-4 mt-2 font-bold text-red-600 underline">Cancelar cobros automáticos</button>}
          </div>}
          {checkoutBlocked && !pendingCheckout && <div role="status" className="w-full rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-slate-700">{checkoutBlockMessage}</div>}
          {pendingCheckout && <div className="w-full rounded-xl border border-amber-100 bg-amber-50 p-4 text-xs text-slate-700">
            <p className="font-bold">Tienes un pago en curso{pendingCheckout.code ? ` con ${pendingCheckout.code}` : ""}.</p>
            <p className="mt-1">Estamos verificando el pago. No inicies otra compra ni cambies de pasarela hasta confirmar el resultado, aunque hayan pasado 15 minutos.</p>
            <div className="mt-2 flex flex-wrap gap-4">{pendingCheckout.response?.init_point && <a className="font-bold text-blue-600 underline" href={pendingCheckout.response.init_point}>Continuar pago</a>}
            {<button type="button" disabled={checkingPayment} onClick={() => checkPendingPayment("")} className="font-bold text-blue-600 underline">{checkingPayment ? "Consultando…" : "Ya pagué / revisar estado"}</button>}
            {pendingCheckout.provider === "epayco" && pendingCheckout.response && <button type="button" onClick={() => { if (window.ePayco) window.ePayco.checkout.configure({ key: pendingCheckout.response.key, test: pendingCheckout.response.test === "true" }).open(pendingCheckout.response); }} className="font-bold text-blue-600 underline">Continuar pago por ePayco</button>}
            </div>
          </div>}
        </div>

        {/* Plan cards */}
        <div className="aorra-plans-cards" style={{ display: "flex", flexWrap: "wrap", gap: "15px", justifyContent: "center", alignItems: "flex-start", width: "100%" }}>
        
        {displayPlans.map((plan) => {
          const isCurrentPlan = subscription?.is_subscribed && subscription?.subscription?.plan_id === plan.id;
          const isExpiredPlan = !isCurrentPlan && subscription?.subscription?.status !== "pending" && subscription?.subscription?.plan_id === plan.id;
          const canCancel = isCurrentPlan && subscription?.can_cancel && subscription?.subscription?.payment_type === "recurring";
          const planFeatures = [...(plan.features || []), ...workspaceFeatures(plan)];
          const benefitsExpanded = Boolean(expandedBenefits[plan.id]);
          const actionLabel = canCancel ? "Plan actual"
            : isExpiredPlan || (isCurrentPlan && subscription?.subscription?.status === "cancelled") ? "Renovar este plan"
            : isCurrentPlan ? "Agregar tiempo a mi plan" : "Elegir este plan";
          
          return (
            <div key={plan.id} className="aorra-plan-card" style={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              padding: "30px 20px",
              gap: "20px",
              width: "100%",
              maxWidth: "330px",
              minHeight: "0",
              background: "#FFFFFF",
              boxShadow: "2px 8px 20px rgba(32, 85, 95, 0.2)",
              borderRadius: "16px",
              position: "relative"
            }}>
              
              <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px", alignItems: "flex-start" }}>
                {/* Badge: Plan Activo / Más popular */}
                <div style={{
                  height: "35px",
                  background: isCurrentPlan ? "#D8FFEC" : isExpiredPlan ? "#FFF0D5" : "#E1E9FC",
                  borderRadius: "12px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 12px",
                  alignSelf: "center"
                }}>
                  <span style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 700,
                    fontSize: "16px",
                    lineHeight: "22px",
                    color: isCurrentPlan ? "#27845B" : isExpiredPlan ? "#9A6700" : "#396BE9"
                  }}>{isCurrentPlan ? "Plan activo" : isExpiredPlan ? "Plan vencido" : plan.is_featured ? "Más popular" : "Recomendado"}</span>
                </div>

                {/* Title & Icon Row */}
                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "19px", width: "100%" }}>
                  <div style={{
                    width: "48px",
                    height: "47.41px",
                    borderRadius: "50%",
                    background: "#DBFBF8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0
                  }}>
                    <span style={{ fontSize: "24px" }}>🚀</span>
                  </div>
                  <h3 style={{
                    margin: 0,
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 800,
                    fontSize: "28px",
                    lineHeight: "38px",
                    color: "#4A4A4A"
                  }}>{plan.name}</h3>
                </div>

                {/* Price Row */}
                <div style={{ display: "flex", flexDirection: "row", alignItems: "baseline", gap: "6px" }}>
                  <span style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 800,
                    fontSize: "36px",
                    lineHeight: "49px",
                    color: "#396BE9"
                  }}>{formatPlanAmount(promotionQuotes[plan.id]?.amount ?? plan.price, plan.currency)}</span>
                  <span style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "19px",
                    color: "#687377"
                  }}> {normalizePlanCurrency(plan.currency)} / {planPeriodLabel(plan.billing_period)}</span>
                </div>

                {/* Description */}
                {paymentGateways.mp_active && <p className="text-xs text-slate-600">Con Mercado Pago: cobro automático cada {recurringInterval(plan)} hasta que canceles.</p>}
                {promotionQuotes[plan.id] && <p className={`text-xs ${promotionQuotes[plan.id].error ? "text-slate-500" : "text-emerald-700"}`}>
                  {promotionQuotes[plan.id].error || <><span className="text-slate-400 line-through">{formatPlanAmount(plan.price, plan.currency)}</span> · Ahorras {formatPlanAmount(promotionQuotes[plan.id].discount_amount, plan.currency)}{promotionQuotes[plan.id].discount_type === "fixed" ? "." : ` (${promotionQuotes[plan.id].percent} %).`}</>}
                </p>}
                <p style={{
                  margin: 0,
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 400,
                  fontSize: "16px",
                  lineHeight: "22px",
                  color: "#687377"
                }}>
                  {plan.description || "Escala al siguiente nivel y administra de forma integral."}
                </p>

                {/* Payment action */}
                <div className="aorra-plan-actions" style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <button
                    type="button"
                    onClick={() => !canCancel && handleSubscribe(plan)}
                    disabled={canCancel || (checkoutBlocked && !pendingCheckout) || checkingPayment || Boolean(processingPlan)}
                    className="aorra-plan-select-button"
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "0px 24px",
                      width: "100%",
                      minHeight: "48px",
                      background: canCancel ? "#62BF92" : "#396BE9",
                      borderRadius: "16px",
                      border: "none",
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 500,
                      fontSize: "16px",
                      color: "#FFFFFF",
                      cursor: canCancel || (checkoutBlocked && !pendingCheckout) || checkingPayment || Boolean(processingPlan) ? "default" : "pointer",
                      transition: "all 0.2s"
                    }}
                  >
                    {processingPlan === plan.id || checkingPayment ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>{checkoutBlocked && !canCancel ? "Revisar y continuar al pago" : actionLabel}{!canCancel && <ArrowRight className="ml-2 h-4 w-4" />}</>
                    )}
                  </button>
                  {isCurrentPlan && !canCancel && <p className="mt-2 text-center text-xs text-slate-500">{paymentGateways.mp_active ? "Con Mercado Pago, el cobro de la nueva suscripción empieza al terminar tu período pagado." : "Los días que te quedan se suman al nuevo pago."}</p>}

                  {canCancel && <button type="button" onClick={openCancellation} className="aorra-cancel-renewal-link">Cancelar cobros automáticos</button>}
                </div>

                {/* Features List */}
                <div id={`benefits-${plan.id}`} className={`aorra-plan-features ${benefitsExpanded ? "expanded" : ""}`} style={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%", marginTop: "4px" }}>
                  {planFeatures.map((feat, fidx) => (
                    <div key={fidx} className="aorra-plan-feature" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "12px" }}>
                      <div style={{
                        width: "26px",
                        height: "26px",
                        borderRadius: "50%",
                        background: "#D8FFEC",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                      }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <polyline points="20 6 9 17 4 12" stroke="#62BF92" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 400,
                        fontSize: "15px",
                        lineHeight: "22px",
                        color: "#687377"
                      }}>{feat}</span>
                    </div>
                  ))}
                </div>
                {planFeatures.length > 3 && <button type="button" className="aorra-plan-features-toggle" aria-controls={`benefits-${plan.id}`} aria-expanded={benefitsExpanded} onClick={() => setExpandedBenefits(current => ({ ...current, [plan.id]: !current[plan.id] }))}>
                  {benefitsExpanded ? "Ver menos beneficios" : `Ver todos los beneficios (${planFeatures.length})`}
                </button>}
              </div>
            </div>
          );
        })}
        </div>

        {/* Help card */}
        <div className="aorra-help-card" style={{
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "flex-start",
          padding: "24px 20px",
          gap: "23px",
          width: "100%",
          minHeight: "316px",
          background: "#CDF7F7",
          boxShadow: "2px 8px 20px rgba(32, 85, 95, 0.2)",
          borderRadius: "16px"
        }}>
          
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
            margin: "0 auto",
            width: "100%",
            position: "relative"
          }}>
            
            {/* Title */}
            <h2 style={{
              margin: 0,
              width: "100%",
              height: "44px",
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 800,
              fontSize: "28px",
              lineHeight: "38px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              color: "#4A4A4A"
            }}>¿Necesitas ayuda?</h2>

            {/* Subtitle */}
            <p style={{
              margin: 0,
              width: "100%",
              height: "50px",
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 400,
              fontSize: "16px",
              lineHeight: "22px",
              textAlign: "center",
              color: "#687377"
            }}>
              Nuestro equipo está listo para atender tus dudas sobre tu plan.
            </p>

            {/* Overlapping Memoji Avatars (Group 1597884452 - Scaled Down) */}
            <div style={{
              position: "relative",
              width: "226px",
              height: "68px",
              marginTop: "8px",
              margin: "8px auto 0"
            }}>
              {/* Avatar 1 */}
              <div style={{
                position: "absolute",
                width: "68px",
                height: "68px",
                left: "0px",
                top: "0px",
                background: "#F0FDFD",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                border: "2px solid #FFFFFF"
              }}>
                <img src="https://api.dicebear.com/7.x/adventurer/svg?seed=Felix" alt="Support 1" style={{ width: "52px", height: "52px", objectFit: "contain" }} />
              </div>

              {/* Avatar 2 (Flipped) */}
              <div style={{
                position: "absolute",
                width: "68px",
                height: "68px",
                left: "42px",
                top: "0px",
                background: "#F0FDFD",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                border: "2px solid #FFFFFF",
                transform: "scaleX(-1)"
              }}>
                <img src="https://api.dicebear.com/7.x/adventurer/svg?seed=Aneka" alt="Support 2" style={{ width: "52px", height: "52px", objectFit: "contain" }} />
              </div>

              {/* Avatar 3 */}
              <div style={{
                position: "absolute",
                width: "68px",
                height: "68px",
                left: "84px",
                top: "0px",
                background: "#F0FDFD",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                border: "2px solid #FFFFFF"
              }}>
                <img src="https://api.dicebear.com/7.x/adventurer/svg?seed=Adrian" alt="Support 3" style={{ width: "52px", height: "52px", objectFit: "contain" }} />
              </div>

              {/* Avatar 4 */}
              <div style={{
                position: "absolute",
                width: "68px",
                height: "68px",
                left: "120px",
                top: "0px",
                background: "#F0FDFD",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                border: "2px solid #FFFFFF"
              }}>
                <img src="https://api.dicebear.com/7.x/adventurer/svg?seed=Elsa" alt="Support 4" style={{ width: "52px", height: "52px", objectFit: "contain" }} />
              </div>

              {/* Avatar 5 */}
              <div style={{
                position: "absolute",
                width: "68px",
                height: "68px",
                left: "158px",
                top: "0px",
                background: "#F0FDFD",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                border: "2px solid #FFFFFF"
              }}>
                <img src="https://api.dicebear.com/7.x/adventurer/svg?seed=Lilith" alt="Support 5" style={{ width: "52px", height: "52px", objectFit: "contain" }} />
              </div>
            </div>

          </div>

          <a
            href="tel:+573219166392"
            style={{ alignSelf: "center", color: "#4A4A4A", fontWeight: 600, fontSize: "18px" }}
          >
            +57 321 9166392
          </a>

          {/* Support WhatsApp */}
          <a
            href="https://wa.me/573219166392"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              padding: "0px 24px",
              width: "100%",
              height: "42px",
              flexShrink: 0,
              background: "#396BE9",
              borderRadius: "16px",
              border: "none",
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 500,
              fontSize: "16px",
              color: "#FFFFFF",
              textDecoration: "none",
              cursor: "pointer",
              transition: "opacity 0.2s"
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = 0.9}
            onMouseLeave={e => e.currentTarget.style.opacity = 1}
          >
            Chatear por WhatsApp
          </a>

        </div>
      </div>


      
      {/* Historial de Pagos Section */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: "0px",
        gap: "20px",
        width: "100%",
        maxWidth: "1250px",
        margin: "60px auto 0"
      }}>
        <h2 style={{
          margin: 0,
          fontFamily: "'Manrope', sans-serif",
          fontWeight: 600,
          fontSize: "18px",
          lineHeight: "22px",
          color: "#1B1F3A"
        }}>Historial de pagos</h2>

        <PaymentHistory entries={history} error={historyError} onRetry={fetchData}
          onDownload={handleDownloadReceipt} downloading={downloadingReceipt} />
      </div>

      <div className="aorra-plan-usage-footer"><FreePlanUsage api={api} variant="plan" /></div>

      {/* Gateway Selection Dialog */}
      <Dialog open={gatewayModalOpen} onOpenChange={setGatewayModalOpen}>
        <DialogContent className="aorra-billing-dialog" hideClose>
          <DialogHeader className="aorra-billing-dialog-header">
            <DialogTitle className="aorra-billing-dialog-title">Elige cómo pagar</DialogTitle>
            <DialogDescription className="aorra-billing-dialog-description">
              Selecciona una opción para {selectedPlanForPayment?.name || "tu plan"}.
            </DialogDescription>
          </DialogHeader>
          <button type="button" className="aorra-billing-dialog-close" aria-label="Cerrar métodos de pago" onClick={() => setGatewayModalOpen(false)}><X aria-hidden="true" /></button>
          <div className="aorra-payment-options">
            <button type="button"
              onClick={() => {
                setGatewayModalOpen(false);
                processEpayco(selectedPlanForPayment);
              }}
              className="aorra-payment-option aorra-payment-option-epayco"
            >
              <span className="aorra-payment-option-icon" aria-hidden="true">e</span>
              <span className="aorra-payment-option-copy">
                <span className="aorra-payment-option-title">ePayco</span>
                <span className="aorra-payment-option-description">Pago único por la vigencia de tu plan.</span>
              </span>
              <ArrowRight className="aorra-payment-option-arrow" aria-hidden="true" />
            </button>
            
            <button type="button"
              onClick={() => {
                setGatewayModalOpen(false);
                processMercadoPago(selectedPlanForPayment);
              }}
              className="aorra-payment-option aorra-payment-option-mercado"
            >
              <span className="aorra-payment-option-icon" aria-hidden="true">M</span>
              <span className="aorra-payment-option-copy">
                <span className="aorra-payment-option-title">Mercado Pago</span>
                <span className="aorra-payment-option-description">Suscripción con renovación automática.</span>
              </span>
              <ArrowRight className="aorra-payment-option-arrow" aria-hidden="true" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={subscriptionModalOpen} onOpenChange={open => { if (!processingPlan) setSubscriptionModalOpen(open); }}>
        <DialogContent className="aorra-billing-dialog" hideClose aria-describedby="mercado-subscription-description">
          <DialogHeader className="aorra-billing-dialog-header"><DialogTitle className="aorra-billing-dialog-title">Confirma tu suscripción</DialogTitle>
            <DialogDescription id="mercado-subscription-description" className="aorra-billing-dialog-description">
              <span className="aorra-billing-plan-summary">
                <span>{subscriptionPlan?.name}</span>
                <strong>{formatPlanPrice(promotionQuotes[subscriptionPlan?.id]?.amount ?? subscriptionPlan?.price, subscriptionPlan?.currency)} cada {recurringInterval(subscriptionPlan)}</strong>
              </span>
              <span>Se renovará automáticamente hasta que canceles los próximos cobros desde Mi plan. Autorizarás tu medio de pago en Mercado Pago.</span>
            </DialogDescription>
          </DialogHeader>
          <button type="button" className="aorra-billing-dialog-close" aria-label="Cerrar suscripción" disabled={Boolean(processingPlan)} onClick={() => setSubscriptionModalOpen(false)}><X aria-hidden="true" /></button>
          <form onSubmit={createMercadoSubscription} className="aorra-billing-form">
            <div className="aorra-billing-field">
              <label htmlFor="mercado-payer-email">Correo de tu cuenta de Mercado Pago</label>
              <input id="mercado-payer-email" type="email" required maxLength={254} autoComplete="email"
                value={payerEmail} onChange={event => setPayerEmail(event.target.value)}
                className="aorra-billing-email" />
            </div>
            {paymentGateways.mp_test_mode && <p className="text-sm text-amber-700">Entorno de pruebas. Usa el correo de un comprador de prueba de Mercado Pago.</p>}
            {subscriptionError && <p role="alert" className="text-sm text-red-600">{subscriptionError}</p>}
            <button type="submit" disabled={Boolean(processingPlan)} className="aorra-billing-primary">
              {processingPlan ? "Preparando suscripción…" : "Continuar a Mercado Pago"}
            </button>
            <button type="button" disabled={checkingPayment || Boolean(processingPlan)} onClick={() => checkPendingPayment("")} className="aorra-billing-status">Revisar una suscripción en curso</button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={cancellationOpen} onOpenChange={open => { if (!cancellingSubscription && !sendingCancellationOtp) setCancellationOpen(open); }}>
        <DialogContent className="aorra-billing-dialog" hideClose>
          <DialogHeader className="aorra-billing-dialog-header">
            <DialogTitle className="aorra-billing-dialog-title">Cancelar renovación automática</DialogTitle>
            <DialogDescription className="aorra-billing-dialog-description">
              Confirmaremos tu identidad con un código enviado a tu WhatsApp. Al cancelar, se detendrán los próximos cobros de Mercado Pago y conservarás el acceso que ya pagaste.
            </DialogDescription>
          </DialogHeader>
          <button type="button" className="aorra-billing-dialog-close" aria-label="Cerrar cancelación" disabled={cancellingSubscription || sendingCancellationOtp} onClick={() => setCancellationOpen(false)}><X aria-hidden="true" /></button>
          <form onSubmit={handleCancelSubscription} className="aorra-billing-form">
            {cancellationChallenge ? <>
              <p className="aorra-billing-dialog-description" role="status">Código enviado a tu WhatsApp. Vence en {Math.ceil(cancellationChallenge.expires_in_seconds / 60)} minutos.</p>
              <div className="aorra-billing-field">
                <label htmlFor="cancellation-otp">Código de confirmación</label>
                <input id="cancellation-otp" className="aorra-billing-email" inputMode="numeric" autoComplete="one-time-code" maxLength={6} pattern="[0-9]{6}" required value={cancellationOtp} onChange={event => setCancellationOtp(event.target.value.replace(/\D/g, "").slice(0, 6))} />
              </div>
              <button type="submit" className="aorra-billing-primary" disabled={cancellingSubscription || cancellationOtp.length !== 6}>{cancellingSubscription ? "Cancelando…" : "Confirmar cancelación"}</button>
              <button type="button" className="aorra-billing-status" disabled={sendingCancellationOtp || cancellingSubscription} onClick={requestCancellationOtp}>{sendingCancellationOtp ? "Enviando…" : "Enviar otro código"}</button>
            </> : <button type="button" className="aorra-billing-primary" disabled={sendingCancellationOtp} onClick={requestCancellationOtp}>{sendingCancellationOtp ? "Enviando código…" : "Enviar código a mi WhatsApp"}</button>}
            {cancellationError && <p role="alert" className="aorra-billing-error">{cancellationError}</p>}
            <button type="button" className="aorra-billing-status" disabled={cancellingSubscription || sendingCancellationOtp} onClick={() => setCancellationOpen(false)}>Mantener suscripción</button>
          </form>
        </DialogContent>
      </Dialog>
      <style>{`
        .aorra-plans-layout {
          display: grid;
          grid-template-columns: 330px minmax(0, 1fr);
          grid-template-areas:
            "cards cards"
            "help promotion";
          gap: 24px 15px;
          width: 100%;
          max-width: 1020px;
          margin: 0 auto;
          align-items: start;
        }
        .aorra-plans-cards {
          grid-area: cards;
        }
        .aorra-plan-features-toggle {
          display: none;
        }
        .aorra-help-card {
          grid-area: help;
        }
        .aorra-promotion-panel {
          grid-area: promotion;
          display: flex;
          flex-direction: column;
          gap: 12px;
          width: 100%;
          min-width: 0;
        }
        @media (max-width: 768px) {
          .aorra-plans-layout {
            grid-template-columns: minmax(0, 330px);
            grid-template-areas:
              "promotion"
              "cards"
              "help";
            justify-content: center;
          }
          .aorra-promotion-panel,
          .aorra-help-card {
            width: 100% !important;
            max-width: 330px;
          }
          .aorra-plans-cards {
            flex-direction: column !important;
            align-items: center !important;
          }
          .aorra-plan-features:not(.expanded) .aorra-plan-feature:nth-child(n + 4) {
            display: none !important;
          }
          .aorra-plan-features-toggle {
            display: inline-flex;
            color: #396BE9;
            font-size: 14px;
            font-weight: 700;
            text-decoration: underline;
            margin-top: 4px;
          }
        }
      `}</style>
    </div>
  );
};

export default PlansPage;
